import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Heart, MessageCircle, Users, Compass, UserCircle } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Badge } from "@/components/ui/badge";
import { motion } from "framer-motion";

const tabs = [
  { to: "/discover", icon: Compass, label: "Discover" },
  { to: "/matches", icon: Users, label: "Matches" },
  { to: "/messages", icon: MessageCircle, label: "Messages", badge: true },
  { to: "/profile/edit", icon: UserCircle, label: "Profile" },
];

const MobileBottomNav = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (!user) return;
    const fetch = async () => {
      const { data: parts } = await supabase
        .from("conversation_participants")
        .select("conversation_id")
        .eq("user_id", user.id);
      if (!parts?.length) { setUnread(0); return; }
      const { count } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .in("conversation_id", parts.map(p => p.conversation_id))
        .neq("sender_id", user.id)
        .is("read_at", null);
      setUnread(count || 0);
    };
    fetch();
    const interval = setInterval(fetch, 15000);
    return () => clearInterval(interval);
  }, [user]);

  // Only show for logged-in users, hide on chat/admin pages
  if (!user) return null;
  const path = location.pathname;
  if (path.startsWith("/admin") || path.startsWith("/chat/")) return null;

  return (
    <nav
      className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur-md border-t border-border"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      <div className="grid grid-cols-4">
        {tabs.map(({ to, icon: Icon, label, badge }) => {
          const active = path === to || (to !== "/" && path.startsWith(to));
          return (
            <Link
              key={to}
              to={to}
              className={`relative flex flex-col items-center justify-center py-2.5 gap-0.5 transition-colors ${
                active ? "text-primary" : "text-muted-foreground"
              }`}
            >
              <div className="relative">
                <Icon className={`h-5 w-5 ${active ? "fill-primary/20" : ""}`} />
                {badge && unread > 0 && (
                  <Badge className="absolute -top-2 -right-2.5 bg-primary text-primary-foreground text-[9px] min-w-[16px] h-4 flex items-center justify-center p-0 rounded-full">
                    {unread > 9 ? "9+" : unread}
                  </Badge>
                )}
              </div>
              <span className="text-[10px] font-medium leading-tight">{label}</span>
              {active && (
                <motion.div
                  layoutId="mobile-tab-indicator"
                  className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-primary rounded-full"
                />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileBottomNav;
